import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Stethoscope } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-up failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: "var(--bg-canvas)" }}>
      <div className="w-full max-w-md fade-up">
        <Link to="/" data-testid="register-brand" className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-semibold text-lg">MedNarrative</span>
        </Link>
        <h1 className="font-display text-3xl font-semibold">Create your account</h1>
        <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
          Generate conversational radiology reports in seconds.
        </p>

        <button
          type="button"
          data-testid="register-google-button"
          onClick={handleGoogle}
          className="mt-8 w-full flex items-center justify-center gap-3 border rounded-md py-2.5 bg-white hover:bg-[#F9F8F6] transition-colors font-medium"
          style={{ borderColor: "var(--color-border)" }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#fbbc05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Sign up with Google
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>or with email</span>
          <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Full name</label>
            <input
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
              style={{ borderColor: "var(--color-border)" }}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Email</label>
            <input
              data-testid="register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
              style={{ borderColor: "var(--color-border)" }}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Password</label>
            <input
              data-testid="register-password-input"
              type="password"
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
              style={{ borderColor: "var(--color-border)" }}
            />
          </div>
          <button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading}
            className="w-full text-white py-2.5 rounded-md font-medium disabled:opacity-60"
            style={{ background: "var(--color-primary)" }}
          >
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="text-sm mt-6 text-center" style={{ color: "var(--color-text-secondary)" }}>
          Already have an account?{" "}
          <Link data-testid="register-go-login" to="/login" className="font-medium" style={{ color: "var(--color-primary)" }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
